import type { MatchState, PlayerState } from "@arcade-hockey/sim";
import { attackDir } from "./net.js";

// Minimum up-ice gain (meters) before we consider a teammate worth passing
// to. Without this floor the carrier passes to anyone "more ahead" — even
// 30 cm — which becomes a back-and-forth pass-spam loop instead of a play.
// 1.5 m is loose enough that breakout passes from the carrier to a teammate
// drifting up the wing usually qualify, but strict enough that pure
// side-passes get rejected.
const MIN_AHEAD_GAIN = 1.5;

// Pick the best teammate to pass to. Returns null when no teammate offers a
// meaningful improvement over keeping the puck. Criteria:
//   (a) at least MIN_AHEAD_GAIN meters further up-ice than the carrier;
//   (b) clear passing lane (no opponent close to the carrier→target line);
//   (c) the teammate is not deep in pressure themselves.
// Among qualifying targets, score = up-ice gain minus a small penalty for
// receiver pressure. This rewards stretch passes into open ice over the
// trailing winger who's about to be hit.
export function bestPassTarget(carrier: PlayerState, state: MatchState): PlayerState | null {
  let best: PlayerState | null = null;
  let bestScore = 0;
  const ad = attackDir(carrier.teamId);
  const laneClearanceRadius = 1.5;

  for (const teammate of state.players) {
    if (teammate.teamId !== carrier.teamId) continue;
    if (teammate.id === carrier.id) continue;

    const ahead = (teammate.pos.x - carrier.pos.x) * ad;
    if (ahead < MIN_AHEAD_GAIN) continue;

    if (laneBlocked(carrier, teammate, state, laneClearanceRadius)) continue;

    // Slight penalty for receivers already under pressure — a pass into a
    // hit isn't worth more than 1.5x its up-ice gain.
    const receiverPressure = nearestOpponentDistance(teammate, state);
    const pressurePenalty = receiverPressure < 2 ? 1.5 : 0;

    const score = ahead - pressurePenalty;
    if (score > bestScore) {
      bestScore = score;
      best = teammate;
    }
  }

  return best;
}

function nearestOpponentDistance(target: PlayerState, state: MatchState): number {
  let best = Number.POSITIVE_INFINITY;
  for (const opp of state.players) {
    if (opp.teamId === target.teamId) continue;
    const dx = opp.pos.x - target.pos.x;
    const dy = opp.pos.y - target.pos.y;
    const d = Math.hypot(dx, dy);
    if (d < best) best = d;
  }
  return best;
}

// Outlet pass: pick the safest teammate to move the puck to, ignoring
// up-ice direction. Used when the carrier is cornered and `bestPassTarget`
// found no forward option — real hockey's "send it back to the D"
// outlet. Score = clearance of the receiver from the nearest opponent,
// minus a penalty if the pass lane itself is blocked.
//
// Critically this enables the faceoff-winning centre to do something
// productive: instead of pushing against the opposing centre forever,
// they send the puck to a defenceman who can set up the breakout.
export function safestOutletTarget(carrier: PlayerState, state: MatchState): PlayerState | null {
  let best: PlayerState | null = null;
  let bestScore = -Number.POSITIVE_INFINITY;
  const laneClearanceRadius = 1.5;

  for (const teammate of state.players) {
    if (teammate.teamId !== carrier.teamId) continue;
    if (teammate.id === carrier.id) continue;
    if (laneBlocked(carrier, teammate, state, laneClearanceRadius)) continue;
    // Receiver must have at least some room — don't outlet into another hit.
    const receiverPressure = nearestOpponentDistance(teammate, state);
    if (receiverPressure < 2) continue;
    if (receiverPressure > bestScore) {
      bestScore = receiverPressure;
      best = teammate;
    }
  }
  return best;
}

function laneBlocked(
  carrier: PlayerState,
  target: PlayerState,
  state: MatchState,
  clearance: number,
): boolean {
  const sx = carrier.pos.x;
  const sy = carrier.pos.y;
  const ex = target.pos.x;
  const ey = target.pos.y;
  const dx = ex - sx;
  const dy = ey - sy;
  const len2 = dx * dx + dy * dy;
  if (len2 < 0.0001) return false;

  // Don't count opponents who are essentially on top of the carrier or the
  // target — at <1.5 m they're engaging the player, not intercepting the
  // pass. Real hockey passes routinely fly past a defender pressed into
  // the puck-carrier's body; the intercept window is the middle of the
  // lane, not at the endpoints.
  const endpointClearance = 1.5;
  const endpointClearance2 = endpointClearance * endpointClearance;

  for (const opp of state.players) {
    if (opp.teamId === carrier.teamId) continue;
    const ox = opp.pos.x - sx;
    const oy = opp.pos.y - sy;
    const dx2ToCarrier = ox * ox + oy * oy;
    if (dx2ToCarrier < endpointClearance2) continue;
    const tx = opp.pos.x - ex;
    const ty = opp.pos.y - ey;
    const dx2ToTarget = tx * tx + ty * ty;
    if (dx2ToTarget < endpointClearance2) continue;

    // Project opp onto the segment; t in [0,1] means it's between endpoints.
    const t = (ox * dx + oy * dy) / len2;
    if (t <= 0 || t >= 1) continue;
    const closestX = sx + dx * t;
    const closestY = sy + dy * t;
    const perpDist = Math.hypot(opp.pos.x - closestX, opp.pos.y - closestY);
    if (perpDist < clearance) return true;
  }
  return false;
}
